import React, { useState, useEffect } from "react"
import styles from "./PaywallContainer.module.scss"
import {
  SubscriptionPlan,
  formatCurrency,
} from "../../pages/SignUpPaywallPages/utils/subscriptionConfig"
import { createCheckoutSession, trackSubscriptionStatus } from "../../services/subscriptionServices"
import ToggleSwitch from "../../components/ToggleSwitch"

interface PaywallContainerProps {
  plan: SubscriptionPlan
  email?: string
  code?: string
}

const PaywallContainer: React.FC<PaywallContainerProps> = ({ plan, email, code }) => {
  const [addUsers, setAddUsers] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [paymentFrequency, setPaymentFrequency] = useState<
    "monthly" | "annual"
  >("annual")

  // Track when user opens the subscription page
  useEffect(() => {
    // Store email in localStorage for later use in the result page
    if (email) {
      localStorage.setItem("userEmail", email)

      // Track subscription_page_open status
      trackSubscriptionStatus("subscription_page_open", email, plan.id, undefined, code)
    }
  }, [email, plan.id, code])

  // Function to handle the checkout process
  const handleCheckout = async () => {
    try {
      setIsLoading(true)

      // Get the checkout URL from the service
      const checkoutUrl = await createCheckoutSession(
        plan.id,
        plan.usersIncluded,
        addUsers,
        plan.additionalUser?.count,
        paymentFrequency,
        email,
        code,
      )

      // Redirect to the Stripe Checkout URL
      window.location.href = checkoutUrl
    } catch (error) {
      console.error("Error creating checkout session:", error)
      alert(
        "There was an error processing your request. Please try again later.",
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddUsersChange = (value: boolean) => {
    setAddUsers(value)
  }

  const handleFrequencyChange = (frequency: "monthly" | "annual") => {
    setPaymentFrequency(frequency)
  }

  const calculateTotalPrice = () => {
    // Get the base price based on payment frequency
    const basePrice = paymentFrequency === "annual" ? plan.annualPrice : plan.monthlyPrice

    // Get the additional user price based on payment frequency
    const additionalUserPrice =
      addUsers && plan.additionalUser 
        ? (paymentFrequency === "annual" 
            ? plan.additionalUser.annualPrice 
            : plan.additionalUser.monthlyPrice)
        : 0

    return basePrice + additionalUserPrice
  }

  // Only show the add users section if the plan has additionalUser
  const showAddUsersSection = !!plan.additionalUser

  return (
    <div className={styles.paywallContainer}>
      <div className={styles.container}>
        <div className={styles.topSection}>
          <div className={styles.headerColumn}>
            <div className={styles.header}>
              <div className={styles.topLabel}>Bridge 2 Technologies</div>
              <h1 className={styles.title}>
                {plan.title}
                {plan.tier && (
                  <span className={styles.tierBadge} style={{ 
                    backgroundColor: plan.tier === "Gold" ? "rgba(245, 192, 42, 1)" : "rgba(229, 228, 226, 1)",
                    color: "rgba(47, 47, 47, 1)",
                    height: "16px",
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                    padding: "0 6px",
                    marginLeft: "10px",
                    fontSize: "9px",
                    fontWeight: 600,
                    borderRadius: "99px",
                  }}>
                    {plan.tier}
                  </span>
                )}
              </h1>
              <div className={styles.subtext}>
                Includes {plan.usersIncluded} users
              </div>
              <div className={styles.description}>
                {plan.description}
              </div>
            </div>
          </div>

          <div className={styles.pricingPanel}>
            <div className={styles.sectionTitle}>
              {paymentFrequency === "annual" ? "Annual" : "Monthly"} Investment
            </div>
            <div className={styles.price}>
              {formatCurrency(
                paymentFrequency === "annual"
                  ? plan.annualPrice
                  : plan.monthlyPrice
              )}
            </div>
            {plan.additionalUser && (
              <div className={styles.additionalUsers}>
                <div className={styles.additionalUsersLabel}>
                  +{plan.additionalUser.count} Additional Users
                </div>
                <div>
                  {formatCurrency(
                    paymentFrequency === "annual"
                      ? plan.additionalUser.annualPrice
                      : plan.additionalUser.monthlyPrice
                  )}
                </div>
              </div>
            )}
            {plan.notes && <div className={styles.notes}>{plan.notes}</div>}
          </div>
        </div>

        <div className={styles.contentColumn}>
          <hr className={styles.divider} />

          <div className={styles.benefitList}>
            {plan.features.map((feature, index) => (
              <div key={index} className={styles.benefitItem}>
                <span className={styles.checkIcon}></span>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className={styles.sidebarColumn}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionHeaderTitle}>
              Subscription Payment Frequency
            </div>
            <hr className={styles.sectionHeaderDivider} />
          </div>

          <div className={styles.sectionContent}>
            <div className={styles.sectionText}>
              Please select your subscription payment frequency
            </div>
            <ToggleSwitch
              value={paymentFrequency}
              leftLabel="Monthly"
              rightLabel="Annual"
              onChange={handleFrequencyChange}
              name="frequency"
              leftValue="monthly"
              rightValue="annual"
            />
          </div>
        </div>

        <div className={styles.investmentSection}>
          <hr className={styles.dividerAlt} />
          <div className={styles.investmentRow}>
            <div className={styles.investmentLabel}>
              Monthly Investment
            </div>
            <div className={styles.investmentValue}>
              {formatCurrency(
                paymentFrequency === "annual" 
                  ? Math.round((plan.annualPrice / 12) * 100) / 100
                  : plan.monthlyPrice
              )}
            </div>
          </div>
        </div>

        {showAddUsersSection && (
          <>
            <div className={styles.sectionContainer}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionHeaderTitle}>Add Users</div>
                <hr className={styles.sectionHeaderDivider} />
              </div>

              <div className={styles.sectionContent}>
                <div className={styles.sectionText}>
                  Would you like to add additional seats to your subscription?
                </div>
                <ToggleSwitch
                  value={addUsers}
                  leftLabel="Yes"
                  rightLabel="No"
                  onChange={handleAddUsersChange}
                  name="addUsers"
                  leftValue={true}
                  rightValue={false}
                />
              </div>
            </div>

            {addUsers && plan.additionalUser && (
              <div className={styles.additionalUsersSection}>
                <hr className={styles.dividerAlt} />
                <div className={styles.additionalUsersRow}>
                  <div className={styles.additionalUsersLabel}>
                    +{plan.additionalUser.count} Additional Users
                  </div>
                  <div className={styles.additionalUsersValue}>
                    {formatCurrency(
                      paymentFrequency === "annual"
                        ? Math.round((plan.additionalUser.annualPrice / 12) * 100) / 100
                        : plan.additionalUser.monthlyPrice
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <div className={styles.totalSection}>
          <div className={styles.totalLabel}>
            Total Monthly Investment
          </div>
          <div className={styles.totalValue}>
            {formatCurrency(
              paymentFrequency === "annual"
                ? Math.round((calculateTotalPrice() / 12) * 100) / 100
                : calculateTotalPrice()
            )}
          </div>
        </div>
      </div>

      <button
        className={styles.actionButton}
        onClick={handleCheckout}
        disabled={isLoading}
      >
        {isLoading ? "Processing..." : "Subscribe"}
      </button>
    </div>
  )
}

export default PaywallContainer
